import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../Context/LanguageContext";
import { FiExternalLink, FiGithub, FiLock, FiChevronDown, FiChevronRight } from "react-icons/fi";
import projects from "../Assets/projects";
import "./projects.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.3 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.5,
      ease: [0.215, 0.61, 0.355, 1],
    },
  }),
};

const ProjectCard = ({ proj, index, t }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      ref={ref}
      className="proj-card"
      variants={cardVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      custom={index}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -6, transition: { duration: 0.2 } }}
    >
      <div className="proj-card__image-wrapper">
        <img src={proj.pic} alt={t(proj.nameKey)} className="proj-card__image" />
        <motion.div
          className="proj-card__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="proj-card__overlay-actions">
            {proj.link && (
              <motion.a
                href={proj.link}
                target="_blank"
                rel="noreferrer"
                className="proj-card__overlay-btn"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiExternalLink size={18} />
                <span>{t("ViewProject")}</span>
              </motion.a>
            )}
            {proj.github && (
              <motion.a
                href={proj.github}
                target="_blank"
                rel="noreferrer"
                className="proj-card__overlay-btn proj-card__overlay-btn--outline"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiGithub size={18} />
                <span>GitHub</span>
              </motion.a>
            )}
          </div>
        </motion.div>
        <div className="proj-card__lang-badge">
          <span className="proj-card__icon">{proj.icon}</span>
          <span>{proj.lang}</span>
        </div>
      </div>

      <div className="proj-card__body">
        <div className="proj-card__header">
          <span className="proj-card__number mono">
            Project_{String(index + 1).padStart(2, "0")}
          </span>
          {!proj.github && (
            <span className="proj-card__private">
              <FiLock size={12} /> {t("Private") || "Private"}
            </span>
          )}
        </div>
        <h3 className="proj-card__name">{t(proj.nameKey)}</h3>
        <p className="proj-card__desc mono">{t(proj.descKey)}</p>

        <div className="proj-card__footer">
          {proj.link ? (
            <a href={proj.link} target="_blank" rel="noreferrer" className="proj-card__link">
              {t("view-project") || "view-project"} <FiExternalLink size={14} />
            </a>
          ) : (
            <span className="proj-card__no-link">{"// no live preview"}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState("All");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const projTypes = ["All", ...new Set(projects.map((p) => p.lang))].sort();
  const filtered = filter === "All" ? projects : projects.filter((p) => p.lang === filter);

  return (
    <motion.div
      className="projects-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      <div className="projects-page__sidebar">
        <button
          className="projects-page__sidebar-toggle"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          <span className="mono">{t("_projects")}</span>
        </button>

        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="projects-page__filter-list"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              {projTypes.map((type) => (
                <button
                  key={type}
                  className={`projects-page__filter-btn${
                    filter === type ? " projects-page__filter-btn--active" : ""
                  }`}
                  onClick={() => setFilter(type)}
                >
                  <span
                    className="filter-dot"
                    style={{
                      background: filter === type ? "var(--accent-cyan)" : "var(--border)",
                    }}
                  />
                  <span className="mono">{type === "All" ? t("All") || "All" : type}</span>
                  <span className="filter-count">
                    {type === "All"
                      ? projects.length
                      : projects.filter((p) => p.lang === type).length}
                  </span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="projects-page__content">
        <div className="projects-page__header">
          <span
            className="mono"
            style={{ color: "var(--text-muted)" }}
          >{`// ${t("_projects")}`}</span>
          <span style={{ color: "var(--accent-cyan)" }}>{` (${filtered.length})`}</span>
        </div>

        <motion.div className="projects-grid" layout>
          <AnimatePresence mode="popLayout">
            {filtered.map((proj, i) => (
              <ProjectCard key={proj.id} proj={proj} index={i} t={t} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Projects;
